import * as d3 from 'd3';
import {useEffect, useId, useRef} from 'react';

export type SparklineTrend = 'up' | 'down' | 'flat';

const getTrendColor = (trend: SparklineTrend) => {
  if (trend === 'up') {
    return 'var(--dig-color__success__base--state-2)';
  }
  if (trend === 'down') {
    return 'var(--dig-color__alert__on-surface)';
  }
  return 'var(--dig-color__text__subtle)';
};

const TARGET_LINE_COLOR = 'var(--dig-color__text__subtle)';

export const Sparkline = ({
  values,
  trend,
  target,
  width = 80,
  height = 24,
}: {
  values: number[];
  trend: SparklineTrend;
  /** When set, draws a horizontal dashed reference line at this y-value. */
  target?: number | null;
  width?: number;
  height?: number;
}) => {
  const svgRef = useRef<SVGSVGElement | null>(null);
  const gradientId = useId().replace(/:/g, '');
  const color = getTrendColor(trend);

  useEffect(() => {
    if (!values?.length) {
      return;
    }

    const margin = {top: 1, right: 1, bottom: 1, left: 1};

    const xScale = d3
      .scaleLinear()
      .domain([0, values.length - 1])
      .range([margin.left, width - margin.right]);

    const yMin = d3.min(values) ?? 0;
    const yMax = d3.max(values) ?? 1;
    const yDomain: [number, number] =
      target != null && !Number.isNaN(target)
        ? [Math.min(yMin, target), Math.max(yMax, target)]
        : [yMin, yMax];

    const yScale = d3
      .scaleLinear()
      .domain(yDomain)
      .range([height - margin.bottom, margin.top]);

    const line = d3
      .line<number>()
      .x((_, i) => xScale(i))
      .y((d) => yScale(d))
      .curve(d3.curveMonotoneX);

    const area = d3
      .area<number>()
      .x((_, i) => xScale(i))
      .y0(height - margin.bottom)
      .y1((d) => yScale(d))
      .curve(d3.curveMonotoneX);

    const svg = d3.select(svgRef.current);
    svg.selectAll('*').remove();

    const gradient = svg
      .append('defs')
      .append('linearGradient')
      .attr('id', gradientId)
      .attr('x1', '0%')
      .attr('y1', '0%')
      .attr('x2', '0%')
      .attr('y2', '100%');

    gradient
      .append('stop')
      .attr('offset', '0%')
      .attr('stop-color', color)
      .attr('stop-opacity', 0.5);

    gradient
      .append('stop')
      .attr('offset', '100%')
      .attr('stop-color', color)
      .attr('stop-opacity', 0);

    svg.append('path').datum(values).attr('d', area).attr('fill', `url(#${gradientId})`);

    if (target != null && !Number.isNaN(target)) {
      svg
        .append('line')
        .attr('x1', margin.left)
        .attr('x2', width - margin.right)
        .attr('y1', yScale(target))
        .attr('y2', yScale(target))
        .attr('stroke', TARGET_LINE_COLOR)
        .attr('stroke-width', 1)
        .attr('stroke-dasharray', '3,2');
    }

    svg
      .append('path')
      .datum(values)
      .attr('d', line)
      .attr('fill', 'none')
      .attr('stroke', color)
      .attr('stroke-width', 1.5);
  }, [values, target, width, height, color, gradientId]);

  if (!values?.length) {
    return null;
  }

  return <svg ref={svgRef} width={width} height={height} aria-hidden />;
};
